import React, { useState } from "react"
import { Params, useParams } from "react-router-dom";
import TabsComp from "components/TabsComp";
import TableComp from "components/Table";
import WeatherStationChart from "./Charts";
import { faFileExcel, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import useWeatherStationStatuses from "hooks/useWeatherStationStatuses";
import PaginationComp from "components/Pagination";
import { IWeatherStationStatus } from "models/WeatherStationStatus";
import CustomTitle from "components/CustomTitle";
import ButtonIcon from "components/ButtonComp";
import useLoading from "hooks/useLoading";
import * as XLSX from 'xlsx';
import CustomLayout from "components/Layout";

const WeatherStation: React.FC = () => {
    const { encryptedId } = useParams<Params>();
    const hookWeatherStationStatus = useWeatherStationStatuses({ weather_station_id: Number(encryptedId) });
    const hookWeatherStationStatusExport = useWeatherStationStatuses({ weather_station_id: Number(encryptedId), pageSize: 1000000 });
    const hookLoading = useLoading({ defaultLoading: false });
    const [activeKey, setActiveKey] = useState("1");

    const handleDownloadXLSX = () => {
        hookLoading.start();
        try {
            const exportData = hookWeatherStationStatusExport.data.map((item: IWeatherStationStatus) => ({
                'ID': item.id,
                'Temperatura': item.temperatura_aire ? item.temperatura_aire + "ºC" : "---",
                'Temperatura máxima': item.temperatura_max ? item.temperatura_max + "ºC" : "---",
                'Temperatura mínima': item.temperatura_min ? item.temperatura_min + "ºC" : "---",
                'Humedad': item.humedad_relativa ? item.humedad_relativa + "%" : "---",
                'Presión Atmosférica': item.presion ? item.presion + "mbar/hPa" : "---",
                'Viento': item.viento_velocidad ? item.viento_velocidad + "m/s" : "---",
                'Punto de rocío': item.punto_rocio ? item.punto_rocio + "ºC" : "---",
                'Fecha': item.created_at,
                'Estado': item.is_online ? "Online" : "Offline"
            }));

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Datos Estación Meteorológica');

            // Generate filename with timestamp
            const fileName = `estacion_meteorologica_${new Date().getTime()}.xlsx`;

            // Save the file
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.error('Error al exportar a XLSX:', error);
        } finally {
            hookLoading.end();
        }
    }

    return (
        <CustomLayout>
            <div style={{ width: "100%", margin: "auto", padding: "20px", borderRadius: "8px" }}>
                <CustomTitle title="Estación meteorológica" />
                <TabsComp
                    activeKey={activeKey}
                    onChange={(key) => setActiveKey(key)}
                    items={[{
                        key: '1',
                        label: 'Datos',
                        children: <div>
                            <CustomTitle
                                className="mb-2"
                                actions={
                                    <ButtonIcon name="Descargar" icon={faFileExcel} variant="success" onClick={handleDownloadXLSX} disabled={hookLoading.isLoading} />
                                }
                            />
                            <TableComp
                                headers={["#", "Temperatura", "Temperatura máxima", "Temperatura mínima", "Humedad", "Presión Atmosférica", "Viento", "Punto de rocío", "Fecha", "Hora", "Estado", "Acciones"]}
                                body={hookWeatherStationStatus.data.map((item: IWeatherStationStatus) => ({
                                    ...item,
                                    temperatura_aire: item.temperatura_aire ? item.temperatura_aire + "ºC" : "---",
                                    temperatura_max: item.temperatura_max ? item.temperatura_max + "ºC" : "---",
                                    temperatura_min: item.temperatura_min ? item.temperatura_min + "ºC" : "---",
                                    humedad_relativa: item.humedad_relativa ? item.humedad_relativa + "%" : "---",
                                    presion: item.presion ? (item.presion).toFixed(2) + "mbar/hPa" : "---",
                                    viento_velocidad: item.viento_velocidad ? item.viento_velocidad + "m/s" : "---",
                                    punto_rocio: item.punto_rocio ? item.punto_rocio + "ºC" : "---",
                                    created_at: item.created_at,
                                    hora: item.created_at
                                }))}
                                keys={["#", "temperatura_aire", "temperatura_max", "temperatura_min", "humedad_relativa", "presion", "viento_velocidad", "punto_rocio", "created_at", "hora", "is_online"]}
                                moment_dates={[{ index: 8, format: "DD/MM/YYYY" }, { index: 9, format: "HH:mm" }]}
                                actions={[{ icon: faInfoCircle, handleClick: (item: any) => { console.log(item); }, variant: "outline-primary", tooltip: "Ver más", disabled: true }]}
                                loading={hookWeatherStationStatus.loading}
                                loadingItems={hookWeatherStationStatus.pageSize}
                                page={hookWeatherStationStatus.page}
                                badges={[{ index: 10 }]}
                                badges_values={[{ col: "is_online", values: [{ color: "#C70039", value: "false", name: "Offline" }, { color: "#008080", value: "true", name: "Online" }] }]}
                            />
                            <PaginationComp
                                page={hookWeatherStationStatus.page}
                                defaultPageSize={hookWeatherStationStatus.pageSize}
                                totalItems={hookWeatherStationStatus.totalItems}
                                onChange={hookWeatherStationStatus.changePage}
                            />
                        </div>
                    }, {
                        key: '2',
                        label: 'Gráficos',
                        children: <div>
                            <WeatherStationChart weather_station_id={Number(encryptedId)} />
                        </div>
                    }]}
                />
            </div>
        </CustomLayout>
    )
}

export default WeatherStation;